<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            <div class="col-md-6">
              <label for="name" class="bmd-label-float">{{ t('topics.name_label') }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.name }"
                id="name"
                v-model.trim="formData.name"
                required
                @blur="validateField('name')"
              />
              <div class="invalid-feedback">{{ errors.name }}</div>
            </div>

            <div class="col-md-6">
              <label class="bmd-label-float">{{ t('topics.subject_label') }}</label>
              <input
                type="text"
                class="form-control"
                :value="subjectName"
                readonly
                disabled
              />
              <input type="hidden" v-model="formData.subject_id" />
            </div>

            <div class="col-md-6">
              <label for="chapter_id" class="bmd-label-float">{{ t('topics.chapter_label') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.chapter_id }"
                id="chapter_id"
                v-model="formData.chapter_id"
                required
              >
                <option value="" disabled>{{ t('topics.select_chapter') }}</option>
                <option 
                  v-for="chapter in allChapters" 
                  :key="chapter.id" 
                  :value="chapter.id"
                >
                  {{ chapter.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.chapter_id }}</div>
            </div>

            <div class="col-md-6">
              <label for="status" class="bmd-label-float">{{ t('topics.status_label') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.status }"
                id="status"
                v-model="formData.status"
                required
              >
                <option value="active">{{ t('topics.active') }}</option>
                <option value="inactive">{{ t('topics.inactive') }}</option>
              </select>
              <div class="invalid-feedback">{{ errors.status }}</div>
            </div>

            <div class="col-12">
              <div class="d-flex gap-3 mt-3">
                <button
                  type="submit"
                  class="btn btn-primary btn-sm flex-grow-1"
                  :disabled="loading"
                >
                  <span v-if="loading">
                    <i class="fas fa-spinner fa-spin ms-2"></i>
                    {{ t('topics.saving') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-save ms-2"></i>
                    {{ submitText || t('topics.save_changes') }}
                  </span>
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm flex-grow-1"
                  @click="handleCancel"
                  :disabled="loading"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ t('topics.cancel') }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, watch, defineEmits, defineProps } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import api from "@/api";

const { t } = useI18n();
const route = useRoute();

const props = defineProps({
  title: String,
  submitText: {
    type: String,
    default: ""
  },
  loading: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  initialData: {
    type: Object,
    default: () => ({
      name: "",
      chapter_id: null,
      subject_id: null,
      status: "active"
    })
  },
  subjectName: {
    type: String,
    default: ""
  }
});

const emit = defineEmits(['submit', 'cancel', 'validation-error', 'update:form-data']);
const toast = useToast();

const tenantId =
  route.query.tenant_id ||
  route.params.tenant_id ||
  localStorage.getItem("tenant_id") ||
  null;

const formData = ref({ ...props.initialData });
const allChapters = ref([]);
const errors = ref({});

watch(formData, (newValue) => {
  emit('update:form-data', newValue);
}, { deep: true });

const fetchChapters = async () => {
  try {
    const response = await api.get('/chapters', {
      params: {
        tenant_id: tenantId || undefined
      }
    });

    allChapters.value = response.data.data;

    if (
      props.isEditMode &&
      formData.value.chapter_id &&
      !allChapters.value.some(c => String(c.id) === String(formData.value.chapter_id))
    ) {
      const chapterResponse = await api.get(`/chapters/${formData.value.chapter_id}`, {
        params: {
          tenant_id: tenantId || undefined
        }
      });

      allChapters.value.unshift(chapterResponse.data.data);
    }
  } catch (error) {
    toast.error(t("topics.chapters_load_failed"));
    console.error("Error fetching chapters:", error);
  }
};

onMounted(() => {
  fetchChapters();
});

watch(() => props.initialData, (newData) => {
  formData.value = { ...newData };
}, { immediate: true, deep: true });

const validateField = (field) => {
  if (!formData.value[field]) {
    errors.value[field] = t('topics.required_field');
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateForm = () => {
  errors.value = {};
  const validations = [
    validateField('name'),
    validateField('chapter_id'),
    validateField('status')
  ];
  return validations.every(valid => valid);
};

const handleSubmit = () => {
  if (!validateForm()) {
    emit('validation-error');
    toast.warning(t('topics.form_correct_errors'));
    return;
  }

  emit('submit', formData.value);
};

const handleCancel = () => {
  emit('cancel');
};
</script>